import type { BlockNoteEditor } from "@blocknote/core";
import {
  DefaultThreadStoreAuth,
  ThreadStore,
  type CommentBody,
  type CommentData,
  type ThreadData,
} from "@blocknote/core/comments";
import {
  IScriptingComment,
  ScriptCommentedOnEnum,
} from "@shared/lib/types/briefCampains";
import { canUseEditorView } from "@shared/ui/Editor/lib/editorComments";
import { getCommentThreadId } from "./commentAnchor";

const toCommentBody = (text: string): CommentBody => [
  { type: "paragraph", content: [{ type: "text", text }] },
];

const toThread = (comment: IScriptingComment, threadId: string): ThreadData => {
  const createdAt = comment.createdAt
    ? new Date(comment.createdAt)
    : new Date();

  const commentData: CommentData = {
    type: "comment",
    id: comment._id || threadId,
    userId: comment.createdBy?._id || "",
    createdAt,
    updatedAt: createdAt,
    reactions: [],
    metadata: {},
    body: toCommentBody(comment.comment || ""),
  };

  return {
    type: "thread",
    id: threadId,
    createdAt,
    updatedAt: createdAt,
    comments: [commentData],
    resolved: false,
    metadata: { quotedText: comment.quotedText },
  };
};

export class ScriptCommentThreadStore extends ThreadStore {
  private threads = new Map<string, ThreadData>();
  private pendingIds = new Set<string>();
  private listeners = new Set<(threads: Map<string, ThreadData>) => void>();

  constructor(userId: string) {
    super(new DefaultThreadStoreAuth(userId || "anonymous", "editor"));
  }

  async addThreadToDocument(options: {
    threadId: string;
    selection: { head: number; anchor: number };
    editor: BlockNoteEditor;
  }): Promise<void> {
    if (!canUseEditorView(options.editor)) return;
    const from = Math.min(options.selection.anchor, options.selection.head);
    const to = Math.max(options.selection.anchor, options.selection.head);
    const markType = options.editor.prosemirrorState.schema.marks.comment;
    if (!markType) return;
    try {
      options.editor.transact((tr) => {
        tr.addMark(
          from,
          to,
          markType.create({ orphan: false, threadId: options.threadId })
        );
      });
    } catch {
      return;
    }
  }

  ensureThread(threadId: string) {
    this.pendingIds.add(threadId);
    if (this.threads.has(threadId)) return;
    const now = new Date();
    this.threads.set(threadId, {
      type: "thread",
      id: threadId,
      createdAt: now,
      updatedAt: now,
      comments: [],
      resolved: false,
      metadata: {},
    });
    this.emit();
  }

  sync(comments: IScriptingComment[]) {
    const next = new Map<string, ThreadData>();

    comments.forEach((comment) => {
      if (
        comment.commentedOn &&
        comment.commentedOn !== ScriptCommentedOnEnum.Thread
      ) {
        return;
      }
      const threadId = getCommentThreadId(comment);
      if (!threadId) return;
      next.set(threadId, toThread(comment, threadId));
      this.pendingIds.delete(threadId);
    });

    this.pendingIds.forEach((threadId) => {
      const existing = this.threads.get(threadId);
      if (existing && !next.has(threadId)) {
        next.set(threadId, existing);
      }
    });

    this.threads = next;
    this.emit();
  }

  private emit() {
    this.listeners.forEach((listener) => listener(this.threads));
  }

  async createThread(options: {
    initialComment: { body: CommentBody; metadata?: unknown };
    metadata?: { id?: string };
  }): Promise<ThreadData> {
    const id = options.metadata?.id || `thread-${Date.now()}`;
    const existing = this.threads.get(id);
    if (existing) return existing;

    const now = new Date();
    const thread: ThreadData = {
      type: "thread",
      id,
      createdAt: now,
      updatedAt: now,
      comments: [
        {
          type: "comment",
          id: `${id}-comment`,
          userId: "",
          createdAt: now,
          updatedAt: now,
          reactions: [],
          body: options.initialComment.body,
          metadata: options.initialComment.metadata ?? {},
        },
      ],
      resolved: false,
      metadata: options.metadata,
    };
    this.pendingIds.add(id);
    this.threads.set(id, thread);
    this.emit();
    return thread;
  }

  async addComment(): Promise<CommentData> {
    throw new Error("Replies use the existing comments sidebar");
  }

  async updateComment(): Promise<void> {
    return;
  }

  async deleteComment(): Promise<void> {
    return;
  }

  async deleteThread(options: { threadId: string }): Promise<void> {
    this.threads.delete(options.threadId);
    this.pendingIds.delete(options.threadId);
    this.emit();
  }

  async resolveThread(options: { threadId: string }): Promise<void> {
    const thread = this.threads.get(options.threadId);
    if (!thread) return;
    this.threads.set(options.threadId, { ...thread, resolved: true });
    this.emit();
  }

  async unresolveThread(options: { threadId: string }): Promise<void> {
    const thread = this.threads.get(options.threadId);
    if (!thread) return;
    this.threads.set(options.threadId, { ...thread, resolved: false });
    this.emit();
  }

  async addReaction(): Promise<void> {
    return;
  }

  async deleteReaction(): Promise<void> {
    return;
  }

  getThread(threadId: string): ThreadData {
    const thread = this.threads.get(threadId);
    if (!thread) {
      throw new Error(`Thread ${threadId} not found`);
    }
    return thread;
  }

  getThreads(): Map<string, ThreadData> {
    return this.threads;
  }

  subscribe(cb: (threads: Map<string, ThreadData>) => void): () => void {
    this.listeners.add(cb);
    return () => {
      this.listeners.delete(cb);
    };
  }
}
